import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
  processed: 'Processado',
  rejected: 'Remetente recusado',
  missing_attachment: 'Anexo ausente',
  failed: 'Falha',
  ignored: 'Ignorado',
  processing: 'Processando'
};

/**
 * Lets an operator allow senders and follow files that arrive by e-mail.
 */
function EmailIntake() {
  const [catalog, setCatalog] = useState(null);
  const [messages, setMessages] = useState([]);
  const [productId, setProductId] = useState('resolve');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [polling, setPolling] = useState(false);

  /**
   * Loads the sender list and the latest intake messages.
   */
  const load = async () => {
    const [senders, history] = await Promise.all([
      api.get('/email-intake/senders'),
      api.get('/email-intake/messages')
    ]);
    setCatalog(senders.data);
    setMessages(history.data.messages || []);
  };

  useEffect(() => {
    let active = true;
    load().catch((error) => {
      if (active) toast.error(error.response?.data?.error || 'Erro ao carregar a entrada de e-mail');
    });
    return () => {
      active = false;
    };
  }, []);

  /**
   * Saves one address as an active sender of the selected product.
   */
  const addSender = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.put('/email-intake/senders', { productId, email, active: true });
      setEmail('');
      await load();
      toast.success('Remetente salvo');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar o remetente');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Turns a sender on or off without removing the address.
   */
  const toggleSender = async (sender) => {
    try {
      await api.put('/email-intake/senders', {
        productId: sender.productId,
        email: sender.email,
        active: !sender.active
      });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar o remetente');
    }
  };

  /**
   * Reads the mailbox immediately.
   */
  const readMailbox = async () => {
    setPolling(true);
    try {
      const response = await api.post('/email-intake/poll');
      await load();
      if (response.data.skipped) toast.error('A caixa ainda não está configurada');
      else toast.success(`Leitura concluída (${response.data.processed} mensagem(ns))`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao ler a caixa');
    } finally {
      setPolling(false);
    }
  };

  /**
   * Asks the server to process one failed or rejected message again.
   */
  const retry = async (id) => {
    try {
      await api.post(`/email-intake/messages/${id}/retry`);
      await load();
      toast.success('Mensagem processada de novo');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao tentar de novo');
    }
  };

  const products = catalog?.products || [];
  const labelOf = (id) => products.find((product) => product.id === id)?.label || id || '—';

  return (
    <Layout headerTitle="Entrada de e-mail" headerSubtitle="Arquivos mensais recebidos em suporte@kakau.co">
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Entrada de e-mail</h1>
            <p className="text-gray-600">
              A caixa {catalog?.mailbox || 'suporte@kakau.co'} só processa um e-mail quando o assunto traz a palavra-chave e o remetente está nesta lista.
            </p>
          </div>
          <button
            type="button"
            onClick={readMailbox}
            disabled={polling}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
          >
            {polling ? 'Lendo caixa...' : 'Ler caixa agora'}
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {products.map((product) => (
            <div key={product.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="font-medium text-gray-900">{product.label}</h2>
              <p className="text-sm text-gray-600 mt-1">Assunto: {product.keyword}</p>
              <p className="text-sm text-gray-600">Anexo: {product.extension}</p>
            </div>
          ))}
        </div>

        <form onSubmit={addSender} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row gap-3">
          <label className="sm:w-64">
            <span className="block text-sm font-medium text-gray-700 mb-1">Produto</span>
            <select
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {products.map((product) => (
                <option key={product.id} value={product.id}>{product.label}</option>
              ))}
            </select>
          </label>
          <label className="flex-1">
            <span className="block text-sm font-medium text-gray-700 mb-1">E-mail do remetente</span>
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            className="sm:self-end px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
          >
            {saving ? 'Salvando...' : 'Adicionar'}
          </button>
        </form>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Produto</th>
                <th className="px-4 py-3">E-mail</th>
                <th className="px-4 py-3">Situação</th>
              </tr>
            </thead>
            <tbody>
              {(catalog?.senders || []).map((sender) => (
                <tr key={sender.id} className="border-t border-gray-100">
                  <td className="px-4 py-3">{labelOf(sender.productId)}</td>
                  <td className="px-4 py-3">{sender.email}</td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => toggleSender(sender)} className="text-primary-700">
                      {sender.active ? 'Ativo' : 'Inativo'}
                    </button>
                  </td>
                </tr>
              ))}
              {catalog && catalog.senders.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-gray-500" colSpan={3}>Nenhum remetente cadastrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Quando</th>
                <th className="px-4 py-3">Produto</th>
                <th className="px-4 py-3">Remetente</th>
                <th className="px-4 py-3">Assunto</th>
                <th className="px-4 py-3">Situação</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => (
                <tr key={message.id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(message.createdAt).toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3">{labelOf(message.productId)}</td>
                  <td className="px-4 py-3">{message.fromEmail || '—'}</td>
                  <td className="px-4 py-3">
                    <div>{message.subject || '—'}</div>
                    {message.errorMessage && <div className="text-red-600 mt-1">{message.errorMessage}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div>{STATUS_LABELS[message.status] || message.status}</div>
                    {['failed', 'missing_attachment', 'rejected'].includes(message.status) && (
                      <button type="button" onClick={() => retry(message.id)} className="text-primary-700 mt-1">
                        Tentar de novo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {messages.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-gray-500" colSpan={5}>Nenhum e-mail processado ainda.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default EmailIntake;
